import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompaniesService } from 'api';
import type { CompanyAlias } from 'api';
import { companiesAction } from 'common/actions';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { useRefreshFirmCompanies } from 'services/hooks/firms/useRefreshFirmCompanies';

export const COMPANY_ALIASES_QUERY_KEY = 'companyAliases';

// Aliases read back from the server always carry their id.
export type SavedCompanyAlias = CompanyAlias & { id: number };
export type CompanyAliasWrite = Pick<CompanyAlias, 'kind' | 'name'>;

const listCompanyAliases = async (companyId: number) =>
  (await CompaniesService.apiCompaniesAliasesList(String(companyId))) as SavedCompanyAlias[];

export const useCompanyAliases = (companyId: number) =>
  useQuery({
    queryFn: () => listCompanyAliases(companyId),
    queryKey: [COMPANY_ALIASES_QUERY_KEY, companyId],
  });

// Each change saves on its own, then refreshes the aliases everywhere they show: this list,
// the company info (Cash Flow Ledger entities) and the firm company list (search).
export const useCompanyAliasMutations = (companyId: number) => {
  const [, dispatch] = useStore() as UseStoreValues;
  const queryClient = useQueryClient();
  const { refreshFirmCompanies } = useRefreshFirmCompanies();

  const onSuccess = async () => {
    try {
      const aliases = await queryClient.fetchQuery({
        queryFn: () => listCompanyAliases(companyId),
        queryKey: [COMPANY_ALIASES_QUERY_KEY, companyId],
        staleTime: 0,
      });
      dispatch(companiesAction.setCompanyAliases(companyId, aliases));
      await refreshFirmCompanies();
    } catch {
      // The change is saved; a failed refresh only leaves these lists stale until the next load.
    }
  };

  // Callers show failures next to the edited name, so the app-wide error toast stays quiet.
  const handlers = { onError: () => undefined, onSuccess };

  return {
    createAlias: useMutation({
      mutationFn: (data: CompanyAliasWrite) => CompaniesService.apiCompaniesAliasesCreate(String(companyId), data),
      ...handlers,
    }),
    removeAlias: useMutation({
      mutationFn: (aliasId: number) => CompaniesService.apiCompaniesAliasesDelete(String(companyId), String(aliasId)),
      ...handlers,
    }),
    // Sends only the changed fields; the generated client types the body as a whole alias.
    updateAlias: useMutation({
      mutationFn: ({ aliasId, data }: { aliasId: number; data: Partial<CompanyAliasWrite> }) =>
        CompaniesService.apiCompaniesAliasesPartialUpdate(String(companyId), String(aliasId), data as CompanyAlias),
      ...handlers,
    }),
  };
};
